/**
 * The registry of ADMIN-FORM refusals this client renders as inline field
 * errors rather than crashes (#100).
 *
 * The API refuses some writes on purpose and names the field to fix: an
 * internal ID already used in the org, an address another account already
 * holds. Those arrive as 409s, and the crash-telemetry gate reported every 4xx
 * it had no explicit exemption for — so prod condition 166 ("A contact with
 * this internal ID already exists in this organization") and 207 ("This email
 * is already associated with another user account") were filed as client
 * crashes while the admin was shown a generic banner.
 *
 * THE RULE, and the reason this is a registry rather than a status check:
 *
 *   A refusal is exempt from the crash reporter only when we can NAME it and
 *   render its UX. Anything else — an unknown code, a known code at a status we
 *   have never seen it at, a bare 409 with no code at all — stays a crash and
 *   still reports.
 *
 * SCOPE — READ BEFORE EXTENDING. This registry governs the QueryClient
 * query/mutation telemetry only (see serverErrorRouting.shouldReportError).
 * It deliberately does NOT cover consume (viewer) writes:
 *
 *   A failed consume write is ALWAYS alert-worthy. We expect consume writes to
 *   succeed and expect the client NOT to write to a closed or denied
 *   assignment, so a write the server refuses is an UNEXPECTED client bug, and
 *   the alert is the instrument that surfaces it. The 409 flood on viewer pages
 *   (condition 19, 57×) is fixed by stopping the EMITTER from firing the doomed
 *   write — never by teaching telemetry to expect it. Adding
 *   `assignment_closed` here would delete the only signal that a viewer is
 *   writing where it must not.
 *
 * Consume classification (what the LEARNER sees for each outcome) already has
 * exactly one home — `src/api/consumeErrorPolicy.ts`. Do not mirror its codes
 * here; a second table is a second thing to drift.
 *
 * WIRE CONTRACT. These envelopes are deliberately absent from vingapi's OpenAPI
 * document (FastAPI does not emit exception bodies), exactly like
 * `mergeDenial.ts` / `heldByLinkedContact.ts` / `contactMethodClaim.ts`.
 *
 * `would_strand_user` and `last_contact_method` (#111) ARE coded —
 * `detail.error` names them, so they are matched by code below like
 * `held_by_linked_contact`. The remaining two plain conflicts are worse:
 * vingapi raises them as bare `ConflictError(message)` with NO machine code,
 * so the only discriminator on the wire is the message. They are matched by
 * pattern here, narrowly and with the status pinned. A stable `error` code on
 * those two responses would let this file drop the patterns — vingapi#246
 * tracks adding them.
 *
 * SECOND DISCRIMINATOR: `violation_type` (#850). vingapi's
 * `PublishedEditViolationError` (src/content/exceptions.py) is a FLAT 422 with
 * NO `code` key: `{"detail": "<sentence>", "field"?: "<path>",
 * "violation_type": "published_edit"}`. It is matched from
 * `VIOLATION_REFUSALS` under the same rule as a code — registered type, status
 * pinned, sentence verbatim — and ONLY when the body carries no `code`: a code
 * always wins, and an unregistered code is not rescued by its violation_type.
 * Its `field` is a PATH (`questions.<qid>.regions`), not a form field, so it
 * rides in `payload.publishedEdit.field` and never in `KnownRefusal.field`.
 * A new violation type the client handles must be registered here; the
 * handledRefusalCodes guard enumerates both tables.
 */
import axios, { type AxiosError } from 'axios';
import { z } from 'zod';
import { dialogMessages } from '@/constants';

/** The form field a refusal is about, when it is about one. */
export type RefusalField = 'internalId' | 'email' | 'phone' | 'name';

export type RefusalKind =
  | 'duplicate_internal_id'
  | 'duplicate_filter_name'
  | 'filter_in_use_by_pending_sends'
  | 'filter_name_length'
  | 'email_in_use'
  | 'phone_in_use'
  | 'held_by_linked_contact'
  | 'address_on_unmanaged_contact'
  | 'method_owned_by_another_account'
  | 'would_strand_user'
  | 'last_contact_method'
  | 'invalid_link'
  | 'share_revised_since'
  | 'premium_component_locked'
  | 'context_not_member'
  | 'share_id_mismatch'
  | 'staff_account_merge_refused'
  | 'address_hidden_by_org_setting'
  | 'internal_contact'
  | 'merge_in_progress'
  | 'same_user'
  | 'merge_refused'
  | 'not_claimable'
  | 'claim_rate_limited'
  | 'verification_rate_limited'
  | 'invitation_sent'
  | 'invitation_sending'
  | 'pair_already_merged'
  | 'sign_in_locked'
  | 'verify_locked'
  | 'staff_merge_confirm_required'
  | 'staff_merge_refused'
  | 'published_edit';

/** The active revision that superseded the one being published (#376). */
export interface ActiveRevisionPayload {
  contentType: 'packet' | 'checklist';
  contentId: number;
  title: string;
  published: string;
  creator: { id: number; name: string };
}

/** Union of per-code payloads carried on coded refusals. */
export type RefusalPayload =
  | { shareRevisedSince: { activeRevision: ActiveRevisionPayload; shareId: number } }
  /** vingapi#1093: the refused address, normalized by the server (E.164 for phones). */
  | { addressHidden: { address: string } }
  /** vingapi#1093 S2a: the Ving support contact(s) the write would have changed. */
  | { internalContact: { contactIds: number[] } }
  /** vingapi#979 (Q-587): the refused address(es), normalized; `address` is the first. */
  | { unmanagedAddress: { address: string; field: 'emails' | 'phones'; addresses: string[] } }
  /** #850: the path the published_edit refusal names (`questions.<qid>.regions`), when it names one. */
  | { publishedEdit: { field?: string } }
  /** vingapi#2113 S2 (#1559 b2): the surviving contact of the already-merged pair. */
  | { pairAlreadyMerged: { primaryId: number } }
  /** vingapi#2447 (Q-663): the token the next Merge press sends, and the server's one line per person. */
  | { staffMergeConfirm: { token: string; lines: { userId: number; line: string }[] } }
  /** vingapi#2447 (Q-663): why the staff merge was refused (`adp_different_people`, `adp_both_integrations`, …). */
  | { staffMergeRefused: { reason: string } };

export interface KnownRefusal {
  kind: RefusalKind;
  /** HTTP status the refusal arrived with. */
  status: number;
  /** vingapi's machine code, when the envelope carried one. */
  code?: string;
  /** vingapi's `violation_type`, when the refusal was matched on it (#850). */
  violationType?: string;
  /** The field the refusal is about — drives inline field errors. */
  field?: RefusalField;
  /** vingapi's own user-facing wording. Never rewrite it; it names the remedy. */
  message: string;
  /**
   * Structured extras parsed from the envelope body, when the code carries
   * them and the shape validates. Undefined when the body is malformed or
   * the code has no extras. (#376 revision conflicts)
   */
  payload?: RefusalPayload;
}

interface CodeEntry {
  kind: RefusalKind;
  /** The status(es) we have mapped this code at. Anything else stays unknown. */
  statuses: number[];
  /**
   * Fixed field; 'noun' to read it from the message ("This email …");
   * 'body' to map the flat body's own `field` (`emails` / `phones`).
   */
  field?: RefusalField | 'noun' | 'body';
  /** The code only counts as this refusal when its payload parses; otherwise it is unknown and reports. */
  requiresPayload?: boolean;
}

/**
 * Codes vingapi puts in `detail.error` (nested envelope) OR — for a
 * `ValidationError(code=…)` — at the top level of a FLAT body whose `detail`
 * is the sentence: `{"detail": "<sentence>", "field": …, "code": "<code>", …}`
 * (vingapi `src/core/exceptions.py` `ValidationError.to_dict`).
 */
const CODE_REFUSALS: Record<string, CodeEntry> = {
  held_by_linked_contact: { kind: 'held_by_linked_contact', statuses: [409] },
  /** vingapi#979 / #1813 (Q-587): a non-admin writes an address that is on a contact
   *  they do not own. Nested envelope; carries nothing about the holder. The
   *  dialog (UnmanagedAddressDialog) builds its own words from `address`. */
  address_on_unmanaged_contact: {
    kind: 'address_on_unmanaged_contact',
    statuses: [409],
    field: 'body',
  },
  method_owned_by_another_account: {
    kind: 'method_owned_by_another_account',
    statuses: [409],
    field: 'noun',
  },
  /** #111: a `move` decision that would leave the holder's user with no
   *  verified way to sign in. */
  would_strand_user: { kind: 'would_strand_user', statuses: [409] },
  /** #111: the address is the contact's only remaining method — refused on
   *  both the move-decision PUT and the detach POST. */
  last_contact_method: { kind: 'last_contact_method', statuses: [409] },
  /** vingapi#498: saving or renaming a contact filter to a name another filter
   *  already uses (case-insensitive). The server message names the existing
   *  filter's spelling. */
  duplicate_filter_name: { kind: 'duplicate_filter_name', statuses: [409], field: 'name' },
  /** vingapi#501 / vingreact#1103: deleting a saved filter that unsent sends still use. The
   *  Manage Saved Filters dialog turns it into its in-place confirm (api/filterInUse.ts). */
  filter_in_use_by_pending_sends: { kind: 'filter_in_use_by_pending_sends', statuses: [409] },
  /** vingapi#372: the auth-link exchange rejected a link (expired, invalid,
   *  already used). A coded refusal the /sso exchange handles inline; the
   *  crash reporter must not fire on it. */
  invalid_link: { kind: 'invalid_link', statuses: [400, 401] },
  /** vingapi#754: publishing a revision cut before the share's current
   *  publish — another admin revised the same share in parallel. */
  share_revised_since: { kind: 'share_revised_since', statuses: [409] },
  /** vingapi#753: editing/deleting/reordering a premium (library-locked)
   *  component on a packet copy. The server sentence names the remedy;
   *  no field — it is a whole-component refusal, not a field error. */
  premium_component_locked: { kind: 'premium_component_locked', statuses: [409] },
  /** vingapi#993: PUT settings with a share.id that is not the writable share.
   *  409 only; the error body carries `share_id` and `writable_share_id`. */
  share_id_mismatch: { kind: 'share_id_mismatch', statuses: [409] },
  /** vingapi#1255: `POST /api/users/merge/force/` refuses to merge a Ving
   *  staff account or move a support seat. `detail.message` is the whole
   *  explanation — "Cannot merge (<reason>[: detail]): …" — with no separate
   *  reason field; MergeUsersDialog shows it verbatim (#815). No field. */
  staff_account_merge_refused: { kind: 'staff_account_merge_refused', statuses: [409] },
  /** vingapi#1093 (flat body): an address typed on invite / contact create /
   *  contact edit belongs to a Ving support contact this scope's
   *  `people.show_internal_contacts` setting hides. `detail` names the
   *  address, scope and setting; `field` is `emails` | `phones`; `address` is
   *  the server-normalized address. One per request (first refused). */
  address_hidden_by_org_setting: {
    kind: 'address_hidden_by_org_setting',
    statuses: [422],
    field: 'body',
  },
  /** vingapi#1093 S2a (flat body): a write (archive/unarchive, merge, method
   *  move) that would change a SHOWN Ving support contact's identity.
   *  `contact_ids` names the contact(s). */
  internal_contact: { kind: 'internal_contact', statuses: [422] },
  /** #822: `POST /api/users/merge/force/` (vingapi user_routers.force_merge,
   *  MergeAlreadyInProgressError) — either user is verifying a different merge.
   *  MergeUsersDialog shows `detail.message`. */
  merge_in_progress: { kind: 'merge_in_progress', statuses: [409] },
  /** #822: `POST /api/users/merge/force/` (SameUserMergeError) — the two ids
   *  name one account. MergeUsersDialog shows `detail.message`. */
  same_user: { kind: 'same_user', statuses: [409] },
  /** #822: `POST /api/users/merge/force/` (uncoded ConflictError from the merge
   *  pipeline, flattened to this code by the router). MergeUsersDialog shows
   *  `detail.message`. */
  merge_refused: { kind: 'merge_refused', statuses: [409] },
  /** #822: `POST /api/users/merge/claim/` (vingapi user_routers.claim,
   *  NotClaimableError). ContactMethodClaimDialog renders it via
   *  `parseClaimRefusal` (contactMethodClaim.ts CLAIM_REFUSALS). */
  not_claimable: { kind: 'not_claimable', statuses: [409] },
  /** #822: `POST /api/users/merge/claim/` (ClaimRateLimitedError).
   *  ContactMethodClaimDialog renders it via `parseClaimRefusal`. */
  claim_rate_limited: { kind: 'claim_rate_limited', statuses: [409] },
  /** #1163 (vingapi#1666 S2): `POST /api/users/merge/{id}/verify/` refuses a
   *  send whose hourly budget is spent (the requester's, the method's or the
   *  merge's); `detail.retry_after` and the `Retry-After` header say when to
   *  try again. Nothing was sent and an earlier code stays valid.
   *  AccountMergeDialog renders it via `parseMergeSendRateLimit`
   *  (mergeSendRefusal.ts): the code step, and when another can be sent. */
  verification_rate_limited: { kind: 'verification_rate_limited', statuses: [429] },
  /** #875a (vingapi#837 2b, InvitationLockedDetail): PUT or DELETE
   *  `/api/invitations/{id}/` on an invitation that was already sent — the row
   *  is an audit record now. `detail.invitation_id` names it. The Scheduled
   *  panel drops the row; the edit dialog shows `detail.message`. */
  invitation_sent: { kind: 'invitation_sent', statuses: [409] },
  /** #875a (vingapi#837 2b): the same routes while the send task is
   *  processing the invitation. Retry after it finishes (it becomes
   *  `invitation_sent`) or is parked (editable again). The row stays and
   *  renders as resolving; `detail.message` is shown. */
  invitation_sending: { kind: 'invitation_sending', statuses: [409] },
  /** vingapi#2113 S2 / #1559 b2: `POST /api/contacts/merge/{id}/execute/` on a
   *  suggestion whose pair is already one contact. Nested envelope
   *  `{detail: {error, message, primary_id}}`; `primary_id` is the survivor.
   *  ContactMergeSuggestionDialog closes silently (identifyPairAlreadyMerged).
   *  Without a valid `primary_id` it is not this refusal and still reports. */
  pair_already_merged: { kind: 'pair_already_merged', statuses: [409], requiresPayload: true },
  /** vingapi#2471 (#1566 a, Q-678): `POST /api/auth/login/` while the identifier is locked
   *  out. 423 + `Retry-After`; nested `{detail: {code, message, retry_after}}` (note `code`,
   *  not `error`). SignIn shows `detail.message` via `signInLockoutWords` (lib/lockoutWords.ts). */
  sign_in_locked: { kind: 'sign_in_locked', statuses: [423] },
  /** vingapi#2471 (#1566 a, Q-678): `POST /api/auth/verify/` after too many wrong codes (sign-in
   *  code AND address verification). Same envelope. VerifyCode, VerifyMethodOfferPanel and
   *  ContactMethodsEditor show `detail.message` via `verifyLockoutWords`. */
  verify_locked: { kind: 'verify_locked', statuses: [423] },
  /** vingapi#2447 / #1959-b (Q-663): `POST /api/contacts/{id}/` by Ving staff whose merge
   *  would raise someone's access. 409 raised at contacts_bulk.py:453 as
   *  `HTTPException(409, detail=payload)`, so the body is NESTED
   *  `{detail: {error, message, confirm, confirm_token, survivor_user_id, user_ids,
   *  people: [{user_id, name, becomes, line}]}}` (staff_merge_wire.py:71-95). The
   *  dialog shows each `people[].line` under the cards; the next Merge press sends
   *  `confirm_token`. Without a token and one person with a line it is not this
   *  refusal and still reports. */
  staff_merge_confirm_required: {
    kind: 'staff_merge_confirm_required',
    statuses: [409],
    requiresPayload: true,
  },
  /** vingapi#2447 / #1959-b (Q-663): the same route, a staff merge the server will not do.
   *  Nested `{detail: {error, message, reason, contact_ids, user_ids}}`
   *  (staff_merge_wire.py:98-119). `message` is the approved sentence for the two
   *  ADP reasons and null for the code-only ones (`superuser_requires_superuser_actor`,
   *  `mixed_orgs`); without a message it is not this refusal and still reports. */
  staff_merge_refused: { kind: 'staff_merge_refused', statuses: [409], requiresPayload: true },
};

/**
 * `violation_type` values vingapi puts at the top level of a FLAT, codeless
 * body (#850). Same contract as a code entry: the status(es) we have mapped it
 * at; anything else stays unknown.
 *
 * Exported READ-ONLY for the #850b contract test, which pins its keys and
 * statuses to the `violation_type` enums declared in schema/vingapi.openapi.json.
 */
export const VIOLATION_REFUSALS: Readonly<
  Record<string, Readonly<{ kind: RefusalKind; statuses: readonly number[] }>>
> = {
  /** vingapi#1306/#1307/#958 (`PublishedEditViolationError`): a shape change
   *  on PUBLISHED content — a question/choice image added, replaced or
   *  removed; hotspot regions, matching targets or ordering order changed —
   *  on packet PUT, component PATCH, checklist PUT and checklist question
   *  PATCH. `detail` names the refusal and the way forward. */
  published_edit: { kind: 'published_edit', statuses: [422] },
};

/** The flat body's `field` (vingapi's payload key) → the form field it is about. */
const BODY_FIELDS: Partial<Record<string, RefusalField>> = { emails: 'email', phones: 'phone' };

/**
 * Codeless conflicts, matched on vingapi's message with the status pinned.
 *
 * `flush_translating_internal_id_conflict` and `provision_user` both raise a
 * bare `ConflictError`, and `register_user` the 400 spelling of the second, so
 * there is nothing else on the wire to key on.
 */
const MESSAGE_REFUSALS: {
  kind: RefusalKind;
  statuses: number[];
  pattern: RegExp;
  field?: RefusalField | 'noun';
  /** When present, the entry only matches if `error.config.url` matches this pattern. */
  endpoint?: RegExp;
}[] = [
  {
    kind: 'duplicate_internal_id',
    statuses: [409],
    pattern: /internal id already exists/i,
    field: 'internalId',
  },
  {
    kind: 'email_in_use',
    statuses: [400, 409],
    pattern: /already associated with another user/i,
    field: 'noun',
  },
  {
    kind: 'context_not_member',
    statuses: [403],
    pattern: /does not have access to (organization|network) \d+/i,
    endpoint: /\/api\/auth\/context\/?$/,
  },
];

/**
 * Consume (viewer) routes. A refusal here is NEVER exempt: see the SCOPE note
 * above — a doomed consume write is a client bug and its alert is how we find
 * the emitter. Checked defensively so that a future admin-shaped message on a
 * view route cannot accidentally silence one.
 *
 * Exported for `saveErrorMessage` (#787), which applies the same exclusion to
 * its 422-detail tier. One definition; do not copy it.
 */
export function isViewRoute(url: string | undefined): boolean {
  return typeof url === 'string' && (url.includes('/api/view/') || url.includes('/api/viewer/'));
}

/** The response body itself when it is a plain object (the flat shape), else null. */
function flatBody(data: unknown): Record<string, unknown> | null {
  return data && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, unknown>) : null;
}

/** FastAPI's `{"detail": …}` wrapper, or the raw body when there is none. */
function refusalBody(data: unknown): Record<string, unknown> | string | null {
  if (typeof data === 'string') return data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const detail = (data as { detail?: unknown }).detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return detail as Record<string, unknown>;
  }
  return data as Record<string, unknown>;
}

function stringField(body: Record<string, unknown> | string | null, key: string): string | undefined {
  if (!body || typeof body === 'string') return undefined;
  const value = body[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** The message to show: the envelope's own wording, else the plain detail. */
function refusalMessage(body: Record<string, unknown> | string | null, fallback: string): string {
  if (typeof body === 'string') return body;
  return stringField(body, 'message') ?? stringField(body, 'detail') ?? fallback;
}

/**
 * Which contact method the wording is about. vingapi phrases both spellings
 * identically apart from the noun ("This email …" / "This phone …"), and the
 * inline error has to land under the right input.
 */
function fieldFromNoun(message: string): RefusalField {
  return /\bphone|\bsms|\btext message/i.test(message) ? 'phone' : 'email';
}

function resolveField(
  declared: RefusalField | 'noun' | 'body' | undefined,
  message: string,
  body: Record<string, unknown> | string | null,
): RefusalField | undefined {
  if (declared === undefined) return undefined;
  if (declared === 'body') {
    const raw = stringField(body, 'field');
    return raw === undefined ? undefined : BODY_FIELDS[raw];
  }
  return declared === 'noun' ? fieldFromNoun(message) : declared;
}

/**
 * The flat `ValidationError(code=…)` envelope: a top-level string `detail`
 * (the sentence) plus a top-level string `code`. Returns the whole body so the
 * code's extras (`field`, `address`, `contact_ids`) can be read, or null when
 * the body is not that shape.
 */
function flatCodedBody(
  data: unknown,
): { code: string; detail: string; body: Record<string, unknown> } | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const body = data as Record<string, unknown>;
  const { detail, code } = body;
  if (typeof detail !== 'string') return null;
  if (typeof code !== 'string' || code.length === 0) return null;
  return { code, detail, body };
}

/**
 * The flat codeless `violation_type` envelope (#850): a top-level string
 * `detail`, a top-level string `violation_type`, and NO `code` key (a code
 * wins). Null when the body is not that shape.
 */
function flatViolationBody(
  data: unknown,
): { violationType: string; detail: string; body: Record<string, unknown> } | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const body = data as Record<string, unknown>;
  if ('code' in body) return null;
  const { detail, violation_type: violationType } = body;
  if (typeof detail !== 'string' || detail.length === 0) return null;
  if (typeof violationType !== 'string' || violationType.length === 0) return null;
  return { violationType, detail, body };
}

// ============================================
// Zod schemas for revision-conflict payloads (#376, R1)
// ============================================

const CreatorSchema = z.object({
  id: z.number(),
  name: z.string(),
});

const ActiveRevisionSchema = z.object({
  content_type: z.enum(['packet', 'checklist']),
  content_id: z.number(),
  title: z.string(),
  published: z.string(),
  creator: CreatorSchema,
});

const ShareRevisedSinceBodySchema = z.object({
  active_revision: ActiveRevisionSchema,
  share_id: z.number(),
});

const AddressHiddenBodySchema = z.object({
  address: z.string().min(1),
});

const UnmanagedAddressBodySchema = z.object({
  address: z.string().min(1),
  field: z.enum(['emails', 'phones']),
  addresses: z.array(z.string()),
});

const PairAlreadyMergedBodySchema = z.object({
  primary_id: z.number().int().positive(),
});

const InternalContactBodySchema = z.object({
  contact_ids: z.array(z.number().int()).min(1),
});

const StaffMergeConfirmBodySchema = z.object({
  confirm_token: z.string().min(1),
  people: z
    .array(z.object({ user_id: z.number().int(), line: z.string().min(1) }))
    .min(1),
});

const StaffMergeRefusedBodySchema = z.object({
  message: z.string().min(1),
  reason: z.string().min(1),
});

function toActiveRevisionPayload(
  raw: z.infer<typeof ActiveRevisionSchema>,
): ActiveRevisionPayload {
  return {
    contentType: raw.content_type,
    contentId: raw.content_id,
    title: raw.title,
    published: raw.published,
    creator: raw.creator,
  };
}

/**
 * Parse the structured extras from a revision-conflict envelope via zod.
 * Returns undefined when the body is malformed — the refusal is still
 * identified (with message) but the call site degrades to message-only.
 */
function parseRefusalPayload(
  code: string,
  body: Record<string, unknown> | string | null,
): RefusalPayload | undefined {
  if (!body || typeof body === 'string') return undefined;
  if (code === 'share_revised_since') {
    const result = ShareRevisedSinceBodySchema.safeParse(body);
    if (!result.success) return undefined;
    return {
      shareRevisedSince: {
        activeRevision: toActiveRevisionPayload(result.data.active_revision),
        shareId: result.data.share_id,
      },
    };
  }
  if (code === 'address_hidden_by_org_setting') {
    const result = AddressHiddenBodySchema.safeParse(body);
    if (!result.success) return undefined;
    return { addressHidden: { address: result.data.address } };
  }
  if (code === 'address_on_unmanaged_contact') {
    const result = UnmanagedAddressBodySchema.safeParse(body);
    if (!result.success) return undefined;
    return { unmanagedAddress: result.data };
  }
  if (code === 'internal_contact') {
    const result = InternalContactBodySchema.safeParse(body);
    if (!result.success) return undefined;
    return { internalContact: { contactIds: result.data.contact_ids } };
  }
  if (code === 'pair_already_merged') {
    const result = PairAlreadyMergedBodySchema.safeParse(body);
    if (!result.success) return undefined;
    return { pairAlreadyMerged: { primaryId: result.data.primary_id } };
  }
  if (code === 'staff_merge_confirm_required') {
    const result = StaffMergeConfirmBodySchema.safeParse(body);
    if (!result.success) return undefined;
    return {
      staffMergeConfirm: {
        token: result.data.confirm_token,
        lines: result.data.people.map((p) => ({ userId: p.user_id, line: p.line })),
      },
    };
  }
  if (code === 'staff_merge_refused') {
    const result = StaffMergeRefusedBodySchema.safeParse(body);
    if (!result.success) return undefined;
    return { staffMergeRefused: { reason: result.data.reason } };
  }
  return undefined;
}

/** A registered code's refusal, or null when it arrived at an unmapped status. */
function fromCodeEntry(
  entry: CodeEntry,
  status: number,
  code: string,
  body: Record<string, unknown> | string,
  message: string,
): KnownRefusal | null {
  if (!entry.statuses.includes(status)) return null;
  const field = resolveField(entry.field, message, body);
  const payload = parseRefusalPayload(code, body);
  if (entry.requiresPayload && !payload) return null;
  return {
    kind: entry.kind, status, code,
    ...(field ? { field } : {}),
    message,
    ...(payload ? { payload } : {}),
  };
}

/**
 * Identify an admin-form refusal this client renders inline, or `null`.
 *
 * `null` is the loud answer: the caller reports it. Callers must never widen a
 * `null` into "probably fine" — add the refusal here, with the surface that
 * renders it, or let it report.
 */
/** The only codes accepted from `detail.code` (vingapi#2471); everything else uses `detail.error`. */
const LOCKOUT_DETAIL_CODES: ReadonlySet<string> = new Set(['sign_in_locked', 'verify_locked']);

export function identifyRefusal(error: unknown): KnownRefusal | null {
  if (!axios.isAxiosError(error)) return null;
  const response = (error as AxiosError).response;
  if (!response) return null; // network/timeout — never a refusal
  if (isViewRoute((error as AxiosError).config?.url)) return null; // consume: see SCOPE
  const status = response.status;

  // 0. A FLAT coded body (`{"detail": "<sentence>", "code": "<code>", …}`,
  //    vingapi#1093). A registered code is matched exactly like a nested one —
  //    status pinned, sentence verbatim. An UNREGISTERED flat code falls
  //    through to the message rules below, which is what it did before this
  //    lookup existed.
  const flat = flatCodedBody(response.data);
  const flatEntry = flat ? CODE_REFUSALS[flat.code] : undefined;
  if (flat && flatEntry) {
    return fromCodeEntry(flatEntry, status, flat.code, flat.body, flat.detail);
  }

  // 0b. A FLAT codeless `violation_type` body (#850). Registered type, status
  //     pinned; anything else falls through exactly as before this lookup.
  const violation = flatViolationBody(response.data);
  const violationEntry = violation ? VIOLATION_REFUSALS[violation.violationType] : undefined;
  if (violation && violationEntry) {
    if (!violationEntry.statuses.includes(status)) return null;
    const path = stringField(violation.body, 'field');
    const payload: RefusalPayload | undefined =
      violationEntry.kind === 'published_edit'
        ? { publishedEdit: path === undefined ? {} : { field: path } }
        : undefined;
    return {
      kind: violationEntry.kind,
      status,
      violationType: violation.violationType,
      message: violation.detail,
      ...(payload ? { payload } : {}),
    };
  }

  const body = refusalBody(response.data);
  if (body === null) return null;

  const fallbackMessage = (error as AxiosError).message ?? '';

  // 1. A machine code in `detail.error`, or (vingapi#2471's lockouts) in `detail.code`.
  //    Only the two lockout kinds are read from `detail.code`; any other `detail.code`
  //    (registered or not) is not a refusal on this path and falls through.
  const codeKey = stringField(body, 'code');
  const code =
    stringField(body, 'error') ?? (codeKey !== undefined && LOCKOUT_DETAIL_CODES.has(codeKey) ? codeKey : undefined);
  if (code) {
    const entry = CODE_REFUSALS[code];
    if (!entry) return null;
    return fromCodeEntry(entry, status, code, body, refusalMessage(body, fallbackMessage));
  }

  // 2. The codeless admin-form conflicts.
  const message = refusalMessage(body, fallbackMessage);
  const url = (error as AxiosError).config?.url;
  for (const entry of MESSAGE_REFUSALS) {
    if (!entry.statuses.includes(status)) continue;
    if (!entry.pattern.test(message)) continue;
    // Endpoint pin: when present, the entry only matches if the request URL matches.
    if (entry.endpoint && (typeof url !== 'string' || !entry.endpoint.test(url))) continue;
    const field = resolveField(entry.field, message, body);
    const kind: RefusalKind =
      entry.kind === 'email_in_use' && field === 'phone' ? 'phone_in_use' : entry.kind;
    return { kind, status, ...(field ? { field } : {}), message };
  }

  // 3. Name length on a saved contact filter (#1810-b). vingapi
  //    contact_filters.py:18-28 answers a FLAT body, no `code`:
  //    `{"detail": "<sentence>", "field": "name"}` at 422. The sentence shown is
  //    the client's approved one (Q-618 a), whatever the server says. Narrowed to
  //    that URL so a 422 naming `name` from any other endpoint cannot match.
  if (status === 422 && typeof url === 'string' && url.includes('/filters/contact/')) {
    if (stringField(flatBody(response.data), 'field') === 'name') {
      return { kind: 'filter_name_length', status, field: 'name', message: dialogMessages.customFilter.nameLengthError };
    }
  }

  return null;
}

/**
 * The `address_on_unmanaged_contact` refusal (#1813, Q-587), or null.
 *
 * `address` is the server's normalized spelling; when the body lacks it (or is
 * malformed) the caller's typed value stands in, so the dialog always names an
 * address. Nothing about the holder is on the wire and none is returned.
 */
export function identifyUnmanagedAddress(
  error: unknown,
  typed?: string,
): { address: string; field: 'emails' | 'phones' | null; addresses: string[] } | null {
  const refusal = identifyRefusal(error);
  if (refusal?.kind !== 'address_on_unmanaged_contact') return null;
  const payload = refusal.payload;
  if (payload && 'unmanagedAddress' in payload) return payload.unmanagedAddress;
  return { address: typed ?? '', field: null, addresses: typed ? [typed] : [] };
}

/**
 * The refusal's field, when it names one. Sugar for the form call sites, which
 * only ever ask "is this MY field's problem, and what do I show?".
 */
export function refusalForField(error: unknown, field: RefusalField): string | null {
  const refusal = identifyRefusal(error);
  return refusal && refusal.field === field ? refusal.message : null;
}

// ============================================
// Revision-conflict helpers (#376)
//
// Components call these instead of checking the kind string directly,
// keeping the codes confined to this file (R5 guard).
// ============================================

/** The share_revised_since payload, or null. */
export interface ShareRevisedSinceResult {
  message: string;
  activeRevision: ActiveRevisionPayload;
  shareId: number;
}

/**
 * Extract a share_revised_since refusal with a valid payload, or null.
 *
 * Returns null both when the error is not share_revised_since AND when
 * the payload is malformed — in the latter case the caller degrades to
 * the message-only path (no fabricated link to /packets/0).
 */
export function identifyShareRevisedSince(error: unknown): ShareRevisedSinceResult | null {
  const refusal = identifyRefusal(error);
  if (refusal?.kind !== 'share_revised_since') return null;
  if (!refusal.payload || !('shareRevisedSince' in refusal.payload)) return null;
  return {
    message: refusal.message,
    activeRevision: refusal.payload.shareRevisedSince.activeRevision,
    shareId: refusal.payload.shareRevisedSince.shareId,
  };
}

// ============================================
// Invitation lock helpers (#875a)
// ============================================

/** A PUT/DELETE `/api/invitations/{id}/` the server refused because the
 *  invitation is no longer editable. */
export interface InvitationLockedResult {
  /** `sent`: an audit record now. `sending`: the send task holds it right now. */
  state: 'sent' | 'sending';
  /** The server's own sentence (`detail.message`), or null when the body carried none. */
  message: string | null;
}

/**
 * Extract an `invitation_sent` / `invitation_sending` refusal, or null.
 *
 * Keys on the code, never the status alone: a bare 409 (or any other status)
 * is not a lock and returns null. `message` is read from the envelope only —
 * never the transport's "Request failed with status code 409" — so the caller
 * can supply its own copy when the server sent none.
 */
export function identifyInvitationLocked(error: unknown): InvitationLockedResult | null {
  const refusal = identifyRefusal(error);
  if (refusal?.kind !== 'invitation_sent' && refusal?.kind !== 'invitation_sending') return null;
  const body = refusalBody((error as AxiosError).response?.data);
  return {
    state: refusal.kind === 'invitation_sent' ? 'sent' : 'sending',
    message: stringField(body, 'message') ?? null,
  };
}

// ============================================
// Already-merged pair (vingapi#2113 S2, #1559 b2)
// ============================================

/**
 * The survivor's id when `POST /api/contacts/merge/{id}/execute/` answered
 * `pair_already_merged`, or null. Null for any other error, the same code at
 * another status, and a body with no valid positive-integer `primary_id`
 * (those stay unknown and report).
 */
export function identifyPairAlreadyMerged(error: unknown): { primaryId: number } | null {
  const refusal = identifyRefusal(error);
  if (refusal?.kind !== 'pair_already_merged') return null;
  if (!refusal.payload || !('pairAlreadyMerged' in refusal.payload)) return null;
  return { primaryId: refusal.payload.pairAlreadyMerged.primaryId };
}

// ============================================
// Staff merge (vingapi#2447, #1959-b, Q-663)
// ============================================

/**
 * `POST /api/contacts/{id}/` answered 409 `staff_merge_confirm_required`: the
 * token the next Merge press sends and the server's own line per person, in
 * server order (never composed from `name`/`becomes`). Null for any other
 * error, the same code at another status, and a body without a token or
 * without at least one person with a non-empty line (those stay unknown and
 * report).
 */
export function identifyStaffMergeConfirm(
  error: unknown,
): { token: string; lines: { userId: number; line: string }[] } | null {
  const refusal = identifyRefusal(error);
  if (refusal?.kind !== 'staff_merge_confirm_required') return null;
  if (!refusal.payload || !('staffMergeConfirm' in refusal.payload)) return null;
  return refusal.payload.staffMergeConfirm;
}

/**
 * `POST /api/contacts/{id}/` answered 409 `staff_merge_refused` with the
 * server's sentence, or null. A refusal whose `message` is null (the code-only
 * reasons) is not this refusal here: it stays unknown, reports, and the dialog
 * shows its generic merge error.
 */
export function identifyStaffMergeRefused(error: unknown): { message: string; reason: string } | null {
  const refusal = identifyRefusal(error);
  if (refusal?.kind !== 'staff_merge_refused') return null;
  if (!refusal.payload || !('staffMergeRefused' in refusal.payload)) return null;
  return { message: refusal.message, reason: refusal.payload.staffMergeRefused.reason };
}
